import { useParams, Link, Navigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import SEOHead from "@/components/SEOHead";
import { blogPosts } from "@/data/blogPosts";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  // Simple markdown-like rendering
  const renderContent = (content: string) => {
    const lines = content.trim().split('\n');
    const elements: JSX.Element[] = [];
    let inList = false;
    let listItems: string[] = [];
    let inTable = false;
    let tableRows: string[][] = [];
    let inCodeBlock = false;
    let codeLines: string[] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
            {listItems.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        const header = tableRows[0];
        const body = tableRows.slice(1);
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto mb-4">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  {header.map((cell, i) => (
                    <th key={i} className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-800">{cell.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                    {row.map((cell, j) => (
                      <td key={j} className="border border-gray-200 px-3 py-2 text-gray-700" dangerouslySetInnerHTML={{ __html: formatInline(cell.trim()) }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }
    };

    const flushCode = () => {
      if (codeLines.length > 0) {
        elements.push(
          <pre key={`code-${elements.length}`} className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4 text-sm">
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      }
    };

    const formatInline = (text: string): string => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm text-gray-800">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
        .replace(/❌/g, '❌').replace(/✅/g, '✅');
    };

    for (const line of lines) {
      const trimmed = line.trim();

      if (trimmed.startsWith('```')) {
        if (inCodeBlock) {
          flushCode();
        } else {
          flushList();
          flushTable();
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeLines.push(line);
        continue;
      }

      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        flushList();
        if (trimmed.replace(/[|\s-]/g, '') === '') continue; // separator row
        const cells = trimmed.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1);
        tableRows.push(cells);
        inTable = true;
        continue;
      } else if (inTable) {
        flushTable();
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        inList = true;
        listItems.push(trimmed.slice(2));
        continue;
      } else if (trimmed.match(/^\d+\.\s/)) {
        inList = true;
        listItems.push(trimmed.replace(/^\d+\.\s/, ''));
        continue;
      } else if (trimmed.startsWith('- [ ]') || trimmed.startsWith('- [x]')) {
        inList = true;
        listItems.push(trimmed.slice(2));
        continue;
      } else {
        flushList();
      }

      if (trimmed === '') continue;

      if (trimmed.startsWith('## ')) {
        elements.push(<h2 key={`h2-${elements.length}`} className="text-2xl font-bold text-gray-800 mt-8 mb-4">{trimmed.slice(3)}</h2>);
      } else if (trimmed.startsWith('### ')) {
        elements.push(<h3 key={`h3-${elements.length}`} className="text-xl font-semibold text-gray-800 mt-6 mb-3">{trimmed.slice(4)}</h3>);
      } else {
        elements.push(<p key={`p-${elements.length}`} className="text-gray-700 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} />);
      }
    }

    flushList();
    flushTable();
    flushCode();

    return elements;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <SEOHead
        title={`${post.title} | CompresorJPEG`}
        description={post.description}
        keywords={post.keywords}
        canonical={`https://compresorjpeg.com/blog/${post.slug}`}
      />

      {/* Article structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            dateModified: post.date,
            author: { "@type": "Organization", name: "CompresorJPEG" },
            publisher: { "@type": "Organization", name: "CompresorJPEG", url: "https://compresorjpeg.com" },
            mainEntityOfPage: `https://compresorjpeg.com/blog/${post.slug}`,
          }),
        }}
      />

      <NavBar />

      <main className="container mx-auto px-4 py-12">
        <article className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-1 text-blue-600 hover:underline mb-6 text-sm">
            <ArrowLeft className="h-4 w-4" /> Volver al blog
          </Link>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{post.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(post.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {post.readTime} de lectura
              </span>
            </div>
          </header>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-10">
            {renderContent(post.content)}
          </div>

          {/* Navigation between posts */}
          <nav className="flex justify-between mt-8 gap-4">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} className="flex items-center gap-2 text-blue-600 hover:underline text-sm">
                <ArrowLeft className="h-4 w-4" /> {prevPost.title}
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} className="flex items-center gap-2 text-blue-600 hover:underline text-sm text-right">
                {nextPost.title} <ArrowRight className="h-4 w-4" />
              </Link>
            ) : <div />}
          </nav>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-white/60 rounded-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">¿Listo para comprimir tus imágenes?</h2>
            <p className="text-gray-600 mb-4">Prueba nuestro compresor gratuito y reduce el tamaño de tus fotos al instante.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Ir al Compresor <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
};

export default BlogPost;
